import { flow } from 'lodash';

import { ROSTER_COLUMN_DESCRIPTIONS } from '../constants';
import SelectableSearchableSortableListCtrl from '../../dashboard/manage/SelectableSearchableSortableListCtrl';

import rosterTableUrl from './rosterTableComponent.html';
import rosterNotesModalHtml from './rosterNotesModal.html';

const GROUP_HEADER_ROW_HEIGHT = 38;
class RosterTableController extends SelectableSearchableSortableListCtrl {
  tableHeaderLabels = ROSTER_COLUMN_DESCRIPTIONS;
  tableEmpty = true;
  hasEvents = false;
  hasFilteredEvents = false || __HEADLESS__;
  orderGroupsBy = (g) => this.orderByFilter(g, this.ShiftUtils.getCalendarGroupSort);

  constructor(
    Authentication,
    ShiftUtils,
    RosterService,
    PersonaCollection,
    Utils,
    UserUtils,
    RosterUtils,
    filterFilter,
    orderByFilter,
    objectAsArrayFilter,
    sdk,
    $uibModal
  ) {
    'ngInject';

    super();
    Object.assign(this, {
      Authentication,
      ShiftUtils,
      RosterService,
      PersonaCollection,
      Utils,
      UserUtils,
      RosterUtils,
      filterFilter,
      orderByFilter,
      objectAsArrayFilter,
      sdk,
      $uibModal,
    });

    this.getShiftDisplayLabels = sdk('model-extensions/getShiftDisplayLabels');
    this.sortBy('employees', 'sortMethod');
  }

  $onChanges(changes) {
    if (!__HEADLESS__ && ('groups' in changes || 'date' in changes)) {
      this.RosterService.setTableData(this.getDataToDisplay());
    }
  }

  get data() {
    return this.RosterService.tableData;
  }

  sortBy(sortMethod, prop) {
    super.sortBy(sortMethod, prop);

    this.RosterService.sortMethod = this[prop];
  }

  getColumnsCount() {
    return this.columns.reportColumns.filter(
      (c) => c.active && !this.shouldHideColumn({ column: c })
    ).length;
  }

  getGroupName(group) {
    return this.ShiftUtils.getGroupTitle(group);
  }

  getRosterRowClass(event, isLast) {
    if (event.isGroupHeader) {
      return `roster-table-group-header ${this.getGroupHeaderClass(event)}`;
    }

    return `roster-table-row ${isLast ? 'no-border' : ''}`;
  }

  getGroupHeaderClass(group) {
    if (group.category === 'position') {
      return this.PersonaCollection.personasDict[group.key]?.color?.replace('#', 'g') ?? '';
    }

    return '';
  }

  getDataToDisplay() {
    if (!this.groups) {
      return [];
    }

    const date = this.Utils.formatDate(this.date);
    this.hasFilteredEvents = false || __HEADLESS__;

    const data = Object.keys(this.groups).reduce((acc, groupKey) => {
      const originalGroup = this.groups[groupKey];
      if (!originalGroup.show) {
        return acc;
      }

      const group = { ...originalGroup };
      const groupName = this.getGroupName(group);
      group.events = Object.keys(group.users).reduce((events, userKey) => {
        const user = group.users[userKey];
        if (date in user.events) {
          const extendedEvents = this._extendEventsForRoster(group, groupName, user.events[date]);
          extendedEvents.forEach((e) => events.push(e));
        }

        return events;
      }, []);

      if (group?.key && group.key !== 'all' && group.events.length) {
        group.events.unshift({
          key: group.key,
          category: group.category,
          type: group.type,
          sortKey: groupName,
          isGroupHeader: true,
        });
      }

      this.hasFilteredEvents = this.hasFilteredEvents || Boolean(group.events.length);
      acc[groupKey] = group;
      return acc;
    }, {});

    return flow([
      this.objectAsArrayFilter.bind(this),
      this.orderGroupsBy.bind(this),
      this.flattenGroups.bind(this),
    ])(data);
  }

  flattenGroups(groups) {
    return groups.reduce((acc, group) => {
      group.events.forEach((e) => acc.push(e));
      return acc;
    }, []);
  }

  getEventRowSize(event) {
    if (event.isGroupHeader) {
      return GROUP_HEADER_ROW_HEIGHT;
    }

    const heightFactors = {
      groups: event.user?.groups,
      tags: event.tags,
    };
    const isExpanded = {
      groups: event.$$groupsExpanded,
      tags: event.$$tagsExpanded,
    };

    return this.UserUtils.getManageTableRowSize(heightFactors, isExpanded, (g) =>
      this.isColumnVisible(g)
    );
  }

  _extendEventsForRoster(group, groupName, events) {
    if (!events) {
      return [];
    }

    return events.map((e) => {
      const { label: shiftTimeLabel, labelHiddenEndTime: shiftHiddenEndTimeLabel } =
        this.getShiftDisplayLabels(e);

      return {
        ...e,
        shiftTimeLabel,
        shiftHiddenEndTimeLabel,
        rosterStatus: this.RosterUtils.getEventStatus(e),
        key: `${group.key}_${e.id}`,
        sortKey: groupName,
        user: {
          ...e.user,
          groups: this.orderByFilter(e.user.groups, 'name'),
        },
        tags: this.orderByFilter(e.tags, 'name'),
      };
    });
  }

  isColumnVisible(columnProperty) {
    const column = this.columns.reportColumns.find((item) => item.property === columnProperty);

    return !column || (!this.shouldHideColumn({ column }) && column.active);
  }

  canDataColumnBeVisible(columnProperty) {
    const column = this.columns.reportColumns.find((item) => item.property === columnProperty);

    return !column || !this.shouldHideColumn({ column });
  }

  openNotesModal(event) {
    if (!event.summary) {
      return;
    }

    this.$uibModal.open({
      templateUrl: rosterNotesModalHtml,
      controllerAs: 'ctrl',
      controller() {
        this.event = event;
      },
    });
  }
}

export default {
  controller: RosterTableController,
  controllerAs: 'ctrl',
  templateUrl: rosterTableUrl,
  bindings: {
    groups: '<',
    columns: '<',
    filtersOpened: '<',
    orderBy: '<',
    date: '<',
    hideScheduleEndTimes: '<',
    clearFilters: '&',
    shouldHideColumn: '&',
    updateEvent: '&',
    refresh: '&',
  },
};
